import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

type TimelineLayerMenuItemProps = {
  title: string;
  subtitle: string;
  shortSubtitle: string;
  thumbnail: string;
  selected?: boolean;
  isMobile?: boolean;
  onClick?: () => void;
};

/** Represents a selectable item in the timeline layer menu. */
export const TimelineLayerMenuListItem = ({
  title,
  subtitle,
  shortSubtitle,
  thumbnail,
  selected = false,
  isMobile = false,
  onClick,
}: TimelineLayerMenuItemProps): JSX.Element => {
  const borderWidth = isMobile ? 4 : 2;
  const imageRadius = isMobile ? 6 : 3;

  return (
    <ButtonBase
      onClick={onClick}
      className={selected ? 'Mui-selected' : undefined}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        borderRadius: 1,
        padding: 0.5,
        '&.Mui-focusVisible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
        },
        ...(!isMobile && {
          '&.Mui-selected': { backgroundColor: 'background.default' },
          '&:hover': { backgroundColor: 'background.hover' },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexShrink: 0,
            border: `${borderWidth}px solid transparent`,
            borderRadius: `${imageRadius + borderWidth}px`,
            ...(selected && { borderColor: 'brand.500' }),
          }}
        >
          <Box
            component="img"
            src={thumbnail}
            alt=""
            sx={{
              width: isMobile ? 64 : 48,
              height: isMobile ? 48 : 36,
              objectFit: 'cover',
              borderRadius: `${imageRadius}px`,
            }}
          />
          {selected && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: 'warmGrey.950',
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16, color: 'white' }} />
            </Box>
          )}
        </Box>

        {isMobile ? (
          <Typography component="p">
            <Typography
              variant="body1"
              color="text.primary"
              fontWeight={600}
              component="span"
              sx={{ display: 'inline' }}
            >
              {title}
            </Typography>{' '}
            <Typography variant="body2" color="text.secondary" component="span">
              {subtitle}
            </Typography>
          </Typography>
        ) : (
          <Stack direction="column" alignItems="baseline" spacing={0.5}>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              component="p"
            >
              {shortSubtitle}
            </Typography>
          </Stack>
        )}
      </Stack>
    </ButtonBase>
  );
};
